import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, ScrollText, ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import { SortHeader, useSort } from "@/components/SortHeader";

const PAGE = 30;

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("tr-TR", {
      day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return (iso || "").slice(0, 16).replace("T", " ");
  }
};

const methodColor = (m) => {
  if (m === "POST") return "bg-emerald-500/15 text-emerald-300";
  if (m === "DELETE") return "bg-rose-500/15 text-rose-300";
  if (m === "PUT" || m === "PATCH") return "bg-amber-500/15 text-amber-300";
  return "bg-secondary text-muted-foreground";
};

export default function AuditLog() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.auditLogs({ skip: page * PAGE, limit: PAGE, search });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    load();
  }, [load]);

  const pages = Math.max(1, Math.ceil(total / PAGE));

  const logSort = useSort(items, {
    ts: (l) => l.ts || "",
    user: (l) => l.user_name || l.user_email || "",
    action: (l) => l.action || "",
    method: (l) => l.method || "",
  });
  const sortedItems = logSort.sorted;

  return (
    <div className="p-5 pt-20 lg:p-10 lg:pt-10">
      <PageHeader
        title="İşlem Logu"
        subtitle="Hangi personelin hangi değişikliği yaptığının kaydı"
        action={
          <Button data-testid="audit-refresh-button" variant="secondary" onClick={load}>
            <RefreshCw className="h-4 w-4" /> <span className="ml-1.5">Yenile</span>
          </Button>
        }
      />

      <div className="relative mb-4 max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          data-testid="audit-search-input"
          value={search}
          onChange={(e) => {
            setPage(0);
            setSearch(e.target.value);
          }}
          placeholder="Personel, işlem veya yol ara..."
          className="pl-9"
        />
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
              <SortHeader label="Tarih" sortId="ts" sort={logSort} className="px-4 py-3" />
              <SortHeader label="Personel" sortId="user" sort={logSort} className="px-4 py-3" />
              <SortHeader label="İşlem" sortId="action" sort={logSort} className="px-4 py-3" />
              <SortHeader label="Metod" sortId="method" sort={logSort} className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={4} className="px-4 py-12 text-center text-muted-foreground">Yükleniyor...</td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-12 text-center text-muted-foreground">
                  <ScrollText className="mx-auto mb-2 h-8 w-8 opacity-40" />
                  Kayıt bulunamadı
                </td>
              </tr>
            ) : (
              sortedItems.map((l) => (
                <tr key={l.id} data-testid="audit-log-row" className="border-b border-border/60 last:border-0 hover:bg-secondary/30">
                  <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">{fmtDate(l.ts)}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{l.user_name || "—"}</div>
                    <div className="text-xs text-muted-foreground">{l.user_email || "—"}</div>
                  </td>
                  <td className="px-4 py-3">{l.action}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-md px-2 py-0.5 text-[11px] font-semibold ${methodColor(l.method)}`}>{l.method}</span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
        <span data-testid="audit-count">{total} kayıt</span>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span>
            {page + 1} / {pages}
          </span>
          <Button variant="secondary" size="sm" disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
